<template>
  <div
    class="z-50 rounded-md w-full flex flex-1 flex-col justify-end"
    :class="{ 'justify-end': !showResources }"
    :style="{
      '--brand-text-on-white-background': readableTextColorOnWhiteBackground,
    }"
  >
    <div class="px-3 pt-6 w-full">
      <team-availability
        :available-agents="availableAgents"
        :has-conversation="!!conversationSize"
        :unread-count="unreadMessageCount"
        @start-conversation="startConversation"
      />
    </div>
    <div class="px-3 pt-3 w-full">
      <order-tracking
        v-if="isOrderTrackingEnabled"
        @start-conversation="startConversation"
      />
    </div>
    <div v-if="showAgentTriggers" class="px-3 pt-3 w-full">
      <agent-bot-triggers @start-conversation="startConversation" />
    </div>
    <resource-list
      @view-article="openArticleInArticleViewer"
      @start-flow="triggerWorkflow"
    />
  </div>
</template>

<script>
import TeamAvailability from 'widget/components/TeamAvailability.vue';
import OrderTracking from 'widget/components/OrderTracking.vue';
import AgentBotTriggers from 'widget/components/AgentBotTriggers.vue';
import ResourceList from './ResourceList.vue';

import { mapGetters } from 'vuex';
import darkModeMixin from 'widget/mixins/darkModeMixin';
import routerMixin from 'widget/mixins/routerMixin';
import configMixin from 'widget/mixins/configMixin';
import { darken, getContrast } from 'color2k';

export default {
  name: 'Home',
  components: {
    TeamAvailability,
    OrderTracking,
    AgentBotTriggers,
    ResourceList,
  },
  mixins: [configMixin, routerMixin, darkModeMixin],
  props: {
    hasFetched: {
      type: Boolean,
      default: false,
    },
    isCampaignViewClicked: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      availableAgents: 'agent/availableAgents',
      activeCampaign: 'campaign/getActiveCampaign',
      conversationSize: 'conversation/getConversationSize',
      unreadMessageCount: 'conversation/getUnreadMessageCount',
      resources: 'inboxChatResources/getResources',
      isOrderTrackingEnabled: 'appConfig/getOrderTrackingEnabled',
      widgetColor: 'appConfig/getWidgetColor',
    }),
    widgetLocale() {
      return this.$i18n.locale || 'en';
    },
    showAgentTriggers() {
      return false;
    },
    showResources() {
      return true;
    },
    defaultLocale() {
      const widgetLocale = this.widgetLocale;
      const { allowed_locales: allowedLocales, default_locale: defaultLocale } =
        this.portal.config;

      // IMPORTANT: Variation strict locale matching, Follow iso_639_1_code
      // If the exact match of a locale is available in the list of portal locales, return it
      // Else return the default locale. Eg: `es` will not work if `es_ES` is available in the list
      if (allowedLocales.includes(widgetLocale)) {
        return widgetLocale;
      }
      return defaultLocale;
    },
    readableTextColorOnWhiteBackground() {
      const contrastLevel = getContrast(this.widgetColor, '#fff');

      const readableColor =
        contrastLevel <= 1.4 ? darken(this.widgetColor, 0.3) : this.widgetColor;

      return readableColor;
    },
  },
  mounted() {
    if (this.portal && this.popularArticles.length === 0) {
      const locale = this.defaultLocale;
      this.$store.dispatch('article/fetch', {
        slug: this.portal.slug,
        locale,
      });
    }
  },
  methods: {
    startConversation() {
      if (this.preChatFormEnabled && !this.conversationSize) {
        return this.replaceRoute('prechat-form');
      }
      return this.replaceRoute('messages');
    },
    openArticleInArticleViewer(link) {
      let linkToOpen = `${link}?show_plain_layout=true`;
      const isDark = this.prefersDarkMode;
      if (isDark) {
        linkToOpen = `${linkToOpen}&theme=dark`;
      }
      this.$router.push({
        name: 'article-viewer',
        params: { link: linkToOpen },
      });
    },
    triggerWorkflow(workflowId) {
      const { websiteToken } = window.chatwootWebChannel;
      this.$store.dispatch('workflows/trigger', { websiteToken, workflowId });
      this.$emit('start-conversation');
      return this.replaceRoute('messages');
    },
  },
};
</script>

<style scoped lang="scss">
.widget-home-bg {
  background: linear-gradient(
    161deg,
    #f4f3f5 8.3%,
    #f4f3f5 25.01%,
    #f4f3f5 58.1%,
    #e4e4fa 94.45%
  );
}

.widget-home-bg-dark {
  background: linear-gradient(
    161deg,
    #26292b 8.3%,
    #26292b 25.01%,
    #1f1f27 58.1%,
    #3c3758 94.45%
  );
}
</style>
